import { RouterRootState } from "connected-react-router";
import React, { FC, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { toast } from "react-toastify";
import SitesTab from "../../../containers/sites-tab";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import { showError } from "../../../services/utilities/ServiceError";
import FullScreenActivityFrame from "../../../components/activity-frame/full-screen-activity-spinner";
import { getListOfSiteIdsFromFilter, shouldUpdateCategory } from "../../../utilities/tabUtils";
import { Filter } from "../../../models/Filter";
import { useTabCount } from "../../../hooks/useCategoryTab";

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const Sites: FC = () => {
    const [selected, setSelected] = useState(0);
    const [busy, setBusy] = useState(false)
    const [busyDeleting, setBusyDeleting] = useState(false)
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const { translate } = useLocale();
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));

    useEffect(() => {
        buildCategoriesData();
    }, []);

    const buildCategoriesData = async () => {
        try {
            setBusy(true);
            setCategories([
                {
                    displayName: translate.byKey(TranslationKeys.sites),
                    heading: translate.byKey(TranslationKeys.sites),
                    value: "",
                    id: 0,
                    count: 1,
                },
            ]);

            setBusy(false);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const onFilterChange = (filters: Filter[]) => {
        const siteIds = getListOfSiteIdsFromFilter(filters);
        updateTabCount(siteIds)
    };

    const { updateTabCount } = useTabCount({
        tableName: "Site",
        categories,
        setCategories,
        customFilters: {id : " >= 2"} // All sites below 2 are system sites and should not be counted
    });

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent = [
                <SitesTab 
                    setBusyDeleting={setBusyDeleting} 
                    categories={categories} 
                    setCategories={setCategories}
                    onFilterChange={onFilterChange}
                    />
            ];
            setTabContent(newTabContent);
        }
    }, [categories]);

    return (
        busyDeleting ? <FullScreenActivityFrame message={translate.byKey(TranslationKeys.deleteInProgress)} /> : (
            <ActivityFrame
                busy={busy}
                className="flex flex-grow overflow-y-hidden">
                <div className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
                    <Breadcrumb
                        path={[
                            {
                                value: "/access-control/configuration",
                                displayName: translate.byKey(TranslationKeys.configuration),
                                disableClick: true
                            },
                            {
                                value: "/access-control/configuration/sites",
                                displayName: translate.byKey(TranslationKeys.sites),
                            },
                        ]}
                        onClick={(item) => history.push(item.value)} />
                    <Tabs
                        className="overflow-y-hidden"
                        tabs={getTabsListFromLabels(categories)}
                        onSelect={(i) => setSelected(i)}
                        selected={selected}
                        onAdd={() => { }}
                        add={false}
                        tabStyle={{}}>
                        {tabContent[selected]}
                        <div className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                            <div className="flex flex-row justify-end button-cluster">
                                <Restricted to={`add.${routeName}.element`}>
                                    <Link
                                        to={{
                                            pathname:
                                                "/access-control/configuration/site/",
                                        }}>
                                        <button className="flex rounded-lg bg-lime text-white p-2 shadow-lg" id='addSiteButton'>
                                            <img
                                                src={plusIcon}
                                                className="px-2 w-8 py-1"
                                                alt="" />
                                            <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                                        </button>
                                    </Link>
                                </Restricted>
                            </div>
                        </div>
                    </Tabs>
                </div>
            </ActivityFrame>
        )
    );
};

export default Sites;
